/**
 * /admin/crm — لوحة CRM (إدارة قاعدة العملاء)
 *
 * - قائمة عملاء بفلتر شريحة + سوق + بحث
 * - الضغط على عميل يفتح Sheet جانبيّ بملفّه ٣٦٠°:
 *   profile, segment, loyalty, orders, recipients, occasions, comms log
 * - زرّ «راسله الآن» يفتح Dialog لإرسال رسالة (واتساب + إشعار داخل التطبيق)
 *   بموافقة الأدمن لكلّ رسالة (لا بثّ آليّ).
 */
import * as React from "react";
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import {
  Search, User, Heart, Crown, Gift, MoonStar, Sparkles,
  ShoppingBag, Send, Loader2, MessageCircle, Bell, Eye, LogIn, CreditCard, Activity, Radio, Copy, Check,
} from "lucide-react";
import { useAdminPageAuth } from "@/hooks/useAdminPageAuth";
import { useIsAdmin } from "@/hooks/useIsAdmin";
import { SiteShell } from "@/components/SiteShell";
import { AdminNav } from "@/components/admin/AdminNav";
import { AdminHelpBox } from "@/components/admin/AdminHelpBox";
import { useLocale } from "@/contexts/LocaleContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { PlaceholderChips } from "@/components/admin/PlaceholderChips";

import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Checkbox } from "@/components/ui/checkbox";
import { ScrollArea } from "@/components/ui/scroll-area";
import { TooltipProvider } from "@/components/ui/tooltip";
import { InfoHint } from "@/components/admin/InfoHint";
import { toast } from "@/lib/toast";
import {
  listCustomers,
  getCustomer360,
  sendCustomerMessage,
} from "@/utils/crmAdmin.functions";
import {
  getCustomerTimeline,
  getCustomerWishlist,
  getLiveVisitorsStats,
  getLiveVisitorsList,
  sendVisitorWhisper,
} from "@/utils/customerActivity.functions";
import { useServerFn } from "@tanstack/react-start";

// SEGMENT_NAME import removed — segment system deprecated
import { AmbassadorCodeBadge } from "@/components/ambassador/AmbassadorCodeBadge";
import { usePhoneLookup } from "@/hooks/usePhoneLookup";
import { CustomerCartPanel } from "@/components/admin/CustomerCartPanel";

export const Route = createFileRoute("/admin/crm")({
  validateSearch: (s: Record<string, unknown>) => ({
    user: typeof s.user === "string" ? s.user : undefined,
    q: typeof s.q === "string" ? s.q : undefined,
  }),
  head: () => ({
    meta: [
      { title: "CRM · Lecmo Admin" },
      { name: "robots", content: "noindex,nofollow" },
    ],
  }),
  component: CrmPage,
});

// SEGMENTS + SEG_LABELS removed — segment system deprecated

const MARKETS = ["all", "AE", "SA", "KW", "QA", "BH", "OM", "IQ", "US", "ROW"] as const;

function CrmPage() {
  const { user, loading: authLoading } = useAdminPageAuth("/admin/crm");
  const isAdmin = useIsAdmin();
  const navigate = useNavigate();
  const { locale } = useLocale();
  const isAr = locale === "ar";
  // L (segment label map) removed — segment system deprecated

  React.useEffect(() => {
    if (!authLoading && user && !isAdmin) navigate({ to: "/" });
  }, [authLoading, user, isAdmin, navigate]);

  const searchParams = Route.useSearch();
  const [search, setSearch] = React.useState(searchParams.q ?? "");
  const [segment, setSegment] = React.useState<string>("all");
  const [market, setMarket] = React.useState<string>("all");
  const [selectedUserId, setSelectedUserId] = React.useState<string | null>(searchParams.user ?? null);
  React.useEffect(() => {
    if (searchParams.user) setSelectedUserId(searchParams.user);
  }, [searchParams.user]);
  React.useEffect(() => {
    if (searchParams.q !== undefined) setSearch(searchParams.q);
  }, [searchParams.q]);
  const [composerOpen, setComposerOpen] = React.useState(false);

  const customersQ = useQuery({
    queryKey: ["crm-customers", search, segment, market],
    queryFn: () =>
      listCustomers({
        data: {
          search: search || undefined,
          segment: segment === "all" ? undefined : segment,
          market: market === "all" ? undefined : market,
          limit: 200,
        },
      }),
  });

  const customers = customersQ.data ?? [];

  // البحث الموحَّد: عندما يبحث الأدمن بأكثر من ٣ أحرف، نفحص أيضاً المصادر الأخرى
  // (طلبات الضيوف، طلبات السفير، موافقات واتساب، الفريق، السائقين) ونعرضها كنتائج إضافيّة.
  const lookupQ = usePhoneLookup(search.trim(), search.trim().length >= 3);
  const customerIds = new Set(customers.map((c) => c.id));
  // نستثني صفوف customer/ambassador المكرَّرة مع القائمة الرئيسيّة
  const extraMatches = (lookupQ.data?.matches ?? []).filter((m) => {
    if (m.source === "customer") return false;
    if (m.source === "ambassador" && m.user_id && customerIds.has(m.user_id)) return false;
    return true;
  });

  if (authLoading || !user || !isAdmin) {
    return (
      <SiteShell>
        <div className="flex min-h-[60vh] items-center justify-center">
          <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
        </div>
      </SiteShell>
    );
  }

  return (
    <SiteShell>
    <TooltipProvider delayDuration={150}>
    <div className="mx-auto w-full max-w-6xl px-4 py-8 sm:px-6">
      <AdminNav />

      <header className="mb-6">
        <h1 className="text-2xl font-semibold tracking-tight inline-flex items-center gap-2">
          {isAr ? "إدارة قاعدة العملاء (CRM)" : "Customer Relationship (CRM)"}
          <InfoHint text={isAr ? "Customer Relationship Management — منظومة إدارة العلاقة مع العملاء: ملفّ ٣٦٠° لكلّ عميل + مراسلة يدويّة." : "Customer Relationship Management — full 360° profile per customer + manual messaging."} />
        </h1>
        <p className="mt-1 text-sm text-muted-foreground">
          {isAr
            ? "ملفّ كامل لكلّ عميل: شريحته، نقاطه، طلباته، مستلموه، ومناسباته. أرسل رسائل واتساب أو إشعارات داخل التطبيق بموافقتك."
            : "Full 360° profile per customer with manual messaging (WhatsApp + in-app)."}
        </p>
      </header>

      <AdminHelpBox title={isAr ? "كيف تستخدم لوحة CRM" : "How to use CRM"}>
        <ul className="list-inside list-disc space-y-1 text-sm text-muted-foreground">
          {(isAr
            ? [
                "اختر شريحة أو سوق لتصفية القائمة، أو ابحث بالاسم/الجوّال.",
                "اضغط على أيّ عميل لرؤية ملفّه الكامل: طلباته، نقاطه، مستلموه، ومناسباته.",
                "زرّ «راسله الآن» يرسل واتساب فوريّ + إشعار يظهر داخل حسابه على الموقع.",
                "كلّ رسالة تُسجَّل في «سجلّ التواصل» داخل ملفّ العميل لتعرف متى آخر مرّة كلّمته.",
              ]
            : [
                "Filter by segment or market, or search by name/phone.",
                "Click any customer to open the 360° profile.",
                "‘Message Now’ sends WhatsApp + in-app inbox notification immediately.",
                "Every message is logged in the customer's communication history.",
              ]
          ).map((line) => (
            <li key={line}>{line}</li>
          ))}
        </ul>
      </AdminHelpBox>

      {/* Live visitors strip */}
      <LiveVisitorsStrip isAr={isAr} />

      {/* Filters */}
      <div className="mb-4 flex flex-wrap items-center gap-2">
        <div className="relative min-w-[220px] flex-1">
          <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
          <Input
            placeholder={isAr ? "اسم أو جوّال…" : "Name or phone…"}
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="pl-9"
          />
        </div>
        <Select value={market} onValueChange={setMarket}>
          <SelectTrigger className="w-[120px]">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {MARKETS.map((m) => (
              <SelectItem key={m} value={m}>{m === "all" ? (isAr ? "كلّ الأسواق" : "All markets") : m}</SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {/* Segment chips removed — segment system deprecated */}

      {/* Customers list */}
      {customersQ.isLoading ? (
        <div className="flex justify-center py-20">
          <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
        </div>
      ) : customers.length === 0 ? (
        <Card>
          <CardContent className="py-12 text-center text-sm text-muted-foreground">
            {isAr ? "لا يوجد عملاء بهذه المعايير." : "No customers match these filters."}
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-2">
          {customers.map((c) => (
            <button
              key={c.id}
              onClick={() => setSelectedUserId(c.id)}
              className="w-full rounded-none border border-border bg-card p-4 text-start transition-colors hover:bg-muted/50"
            >
              <div className="flex items-center justify-between gap-3">
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2">
                    <span className="truncate font-medium">{c.display_name ?? "—"}</span>
                    {c.preferred_market && (
                      <Badge variant="outline" className="text-[10px]">{c.preferred_market}</Badge>
                    )}
                  </div>
                  <div className="mt-1 flex flex-wrap gap-x-3 gap-y-1 text-xs text-muted-foreground">
                    {c.phone_e164 && <span>📱 {c.phone_e164}</span>}
                    {c.loyalty && <span>⭐ {c.loyalty.points_balance}</span>}
                  </div>
                </div>
              </div>
            </button>
          ))}
        </div>
      )}

      {/* نتائج إضافية من مصادر أخرى (طلبات ضيوف، طلبات سفير، موافقات واتساب، فريق، سائقون) */}
      {extraMatches.length > 0 && (
        <div className="mt-6">
          <h2 className="mb-2 text-sm font-semibold text-muted-foreground">
            {isAr ? `نتائج أخرى مرتبطة بالبحث (${extraMatches.length})` : `Other matches (${extraMatches.length})`}
          </h2>
          <div className="space-y-2">
            {extraMatches.map((m, i) => {
              const inner = (
                <div className="flex items-center justify-between gap-3">
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2">
                      <Badge variant="secondary" className="text-[10px]">
                        {isAr ? m.label_ar : m.label_en}
                      </Badge>
                      <span className="truncate text-sm font-medium">
                        {m.display_name ?? "—"}
                      </span>
                      {m.badge_ar && (
                        <Badge variant="outline" className="text-[10px] ltr">
                          {isAr ? m.badge_ar : m.badge_en}
                        </Badge>
                      )}
                    </div>
                    <div className="mt-1 text-xs text-muted-foreground ltr">
                      📱 {m.matched_phone}
                    </div>
                  </div>
                </div>
              );
              const cls = "block w-full rounded-none border border-border bg-card p-4 text-start transition-colors hover:bg-muted/50";
              return m.link ? (
                <a key={i} href={m.link} className={cls}>{inner}</a>
              ) : (
                <div key={i} className={cls}>{inner}</div>
              );
            })}
          </div>
        </div>
      )}



      {/* 360° Sheet */}
      <Customer360Sheet
        userId={selectedUserId}
        onClose={() => setSelectedUserId(null)}
        onMessage={() => setComposerOpen(true)}
        labels={{ all: isAr ? "الكلّ" : "All" }}
        isAr={isAr}
      />

      {/* Composer */}
      <MessageComposer
        userId={selectedUserId}
        open={composerOpen}
        onClose={() => setComposerOpen(false)}
        isAr={isAr}
      />
    </div>
    </TooltipProvider>
    </SiteShell>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Copyable code chip
// ─────────────────────────────────────────────────────────────────────────────
// CopyableCode removed — use <AmbassadorCodeBadge /> from src/components/ambassador/AmbassadorCodeBadge

// ─────────────────────────────────────────────────────────────────────────────
// Customer 360° Sheet
// ─────────────────────────────────────────────────────────────────────────────
function Customer360Sheet({
  userId,
  onClose,
  onMessage,
  labels,
  isAr,
}: {
  userId: string | null;
  onClose: () => void;
  onMessage: () => void;
  labels: Record<string, string>;
  isAr: boolean;
}) {
  const q = useQuery({
    queryKey: ["crm-360", userId],
    queryFn: () => getCustomer360({ data: { userId: userId! } }),
    enabled: !!userId,
  });

  const data = q.data;

  const lastOrder = data?.orders?.[0];
  const joinDate = data?.profile?.created_at
    ? new Date(data.profile.created_at).toLocaleDateString(isAr ? "ar-EG" : "en-US", {
        year: "numeric",
        month: "short",
        day: "numeric",
      })
    : "—";

  return (
    <Sheet open={!!userId} onOpenChange={(o) => !o && onClose()}>
      <SheetContent className="w-full overflow-y-auto sm:max-w-xl">
        <SheetHeader>
          <SheetTitle>{data?.profile?.display_name ?? "—"}</SheetTitle>
        </SheetHeader>

        {q.isLoading || !data ? (
          <div className="flex justify-center py-10">
            <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
          </div>
        ) : (
          <div className="mt-4 space-y-4">
            {/* ── ملخّص ثابت موحَّد — صفوف مرتبة ── */}
            <Card>
              <CardContent className="divide-y divide-border p-0">
                {/* الصف الأول: الاسم */}
                <div className="flex items-center justify-between gap-2 px-4 py-3">
                  <span className="text-xs text-muted-foreground">{isAr ? "الاسم" : "Name"}</span>
                  <span className="text-sm font-medium">{data.profile?.display_name ?? "—"}</span>
                </div>

                {/* الصف الثاني: كود السفير */}
                {data.ambassador?.code && (
                  <div className="flex items-center justify-between gap-2 px-4 py-3">
                    <span className="text-xs text-muted-foreground">{isAr ? "كود السفير" : "Ambassador code"}</span>
                    <AmbassadorCodeBadge code={data.ambassador.code} tier={data.ambassador.tier} />
                  </div>
                )}

                {/* الصف الثالث: تاريخ التحاقه */}
                <div className="flex items-center justify-between gap-2 px-4 py-3">
                  <span className="text-xs text-muted-foreground">{isAr ? "تاريخ التحاقه" : "Joined"}</span>
                  <span className="text-sm font-medium">{joinDate}</span>
                </div>

                {/* الصف الرابع: السوق */}
                <div className="flex items-center justify-between gap-2 px-4 py-3">
                  <span className="text-xs text-muted-foreground">{isAr ? "السوق" : "Market"}</span>
                  <span className="text-sm font-medium">{(data.profile?.preferred_market as string) ?? "—"}</span>
                </div>

                {/* الصف الخامس: نقاط الولاء */}
                <div className="flex items-center justify-between gap-2 px-4 py-3">
                  <span className="text-xs text-muted-foreground">{isAr ? "نقاط الولاء" : "Loyalty points"}</span>
                  <span className="text-sm font-medium">{String(data.loyalty?.points_balance ?? 0)}</span>
                </div>

                {/* الصف السادس: إجمالي مكتسب */}
                <div className="flex items-center justify-between gap-2 px-4 py-3">
                  <span className="text-xs text-muted-foreground">{isAr ? "إجماليّ مكتسب" : "Lifetime earned"}</span>
                  <span className="text-sm font-medium">{String(data.loyalty?.lifetime_earned ?? 0)}</span>
                </div>

                {/* الصف السابع: الهاتف */}
                <div className="flex items-center justify-between gap-2 px-4 py-3">
                  <span className="text-xs text-muted-foreground">{isAr ? "الهاتف" : "Phone"}</span>
                  <span className="text-sm font-medium ltr">{(data.profile as { phone_e164?: string | null } | null)?.phone_e164 ?? "—"}</span>
                </div>

                {/* الصف الثامن: الإيميل */}
                <div className="flex items-center justify-between gap-2 px-4 py-3">
                  <span className="text-xs text-muted-foreground">{isAr ? "الإيميل" : "Email"}</span>
                  <span className="text-sm font-medium ltr">{(data as { email?: string | null }).email ?? "—"}</span>
                </div>

                {/* الصف التاسع: آخر طلب */}
                <div className="px-4 py-3">
                  <span className="block text-xs text-muted-foreground mb-1.5">{isAr ? "آخر طلب" : "Last order"}</span>
                  {lastOrder ? (
                    <div className="flex items-center justify-between gap-2 text-sm">
                      <span className="font-medium">{lastOrder.order_name}</span>
                      <span className="text-muted-foreground">
                        {lastOrder.total_amount} {lastOrder.currency} · {new Date(lastOrder.shopify_created_at ?? Date.now()).toLocaleDateString(isAr ? "ar-EG" : "en-US", { month: "short", day: "numeric" })}
                      </span>
                    </div>
                  ) : (
                    <span className="text-sm text-muted-foreground">{isAr ? "لا طلبات بعد." : "No orders yet."}</span>
                  )}
                </div>
              </CardContent>
            </Card>

            <Button onClick={onMessage} className="w-full">
              <Send className="me-2 h-4 w-4" />
              {isAr ? "راسله الآن" : "Message now"}
            </Button>

            {/* ── تفاصيل أكثر — Accordion ── */}
            <Accordion type="single" collapsible className="w-full">
              <AccordionItem value="activity">
                <AccordionTrigger>{isAr ? "النشاط" : "Activity"}</AccordionTrigger>
                <AccordionContent>
                  <ActivityTimelineTab userId={userId!} isAr={isAr} />
                </AccordionContent>
              </AccordionItem>

              <AccordionItem value="wishlist">
                <AccordionTrigger>{isAr ? "قائمة الأمنيات" : "Wishlist"}</AccordionTrigger>
                <AccordionContent className="space-y-2">
                  <WishlistTab userId={userId!} isAr={isAr} />
                </AccordionContent>
              </AccordionItem>

              <AccordionItem value="cart">
                <AccordionTrigger>{isAr ? "السلّة" : "Cart"}</AccordionTrigger>
                <AccordionContent className="space-y-2">
                  <CustomerCartPanel userId={userId!} isAr={isAr} />
                </AccordionContent>
              </AccordionItem>

              <AccordionItem value="orders">
                <AccordionTrigger>
                  {isAr ? `كلّ الطلبات (${data.orders.length})` : `All orders (${data.orders.length})`}
                </AccordionTrigger>
                <AccordionContent className="space-y-2">
                  {data.orders.length === 0 ? (
                    <EmptyHint text={isAr ? "لا طلبات بعد." : "No orders yet."} />
                  ) : (
                    data.orders.map((o: any) => (
                      <div key={o.shopify_order_id} className="rounded border border-border p-3 text-xs">
                        <div className="flex items-center justify-between">
                          <span className="font-medium">{o.order_name}</span>
                          <Badge variant={o.is_gift ? "default" : "outline"} className="text-[10px]">
                            {o.is_gift ? (isAr ? "هديّة" : "Gift") : (isAr ? "ذاتيّ" : "Self")}
                          </Badge>
                        </div>
                        <div className="mt-1 text-muted-foreground">
                          {o.total_amount} {o.currency} · {o.market} · {o.financial_status ?? "—"}
                        </div>
                      </div>
                    ))
                  )}
                </AccordionContent>
              </AccordionItem>

              <AccordionItem value="recipients">
                <AccordionTrigger>
                  {isAr ? `المستلمون (${data.recipients.length})` : `Recipients (${data.recipients.length})`}
                </AccordionTrigger>
                <AccordionContent className="space-y-2">
                  {data.recipients.length === 0 ? (
                    <EmptyHint text={isAr ? "لا مستلمون محفوظون." : "No saved recipients."} />
                  ) : (
                    data.recipients.map((r: any) => (
                      <div key={r.id} className="rounded border border-border p-3 text-xs">
                        <div className="font-medium">{r.recipient_name ?? "—"}</div>
                        <div className="mt-0.5 text-muted-foreground">
                          {r.recipient_phone} · {r.country ?? "—"}
                        </div>
                        {r.last_gift_product_title && (
                          <div className="mt-1 text-muted-foreground">
                            {isAr ? "آخر هديّة: " : "Last gift: "}
                            {r.last_gift_product_title}
                          </div>
                        )}
                      </div>
                    ))
                  )}
                </AccordionContent>
              </AccordionItem>

              <AccordionItem value="occasions">
                <AccordionTrigger>
                  {isAr ? `المناسبات (${data.occasions.length})` : `Occasions (${data.occasions.length})`}
                </AccordionTrigger>
                <AccordionContent className="space-y-2">
                  {data.occasions.length === 0 ? (
                    <EmptyHint text={isAr ? "لا مناسبات محفوظة." : "No occasions saved."} />
                  ) : (
                    data.occasions.map((o: any) => (
                      <div key={o.id} className="rounded border border-border p-3 text-xs">
                        <span className="font-medium">{o.custom_label ?? o.occasion_type}</span>
                        <span className="ms-2 text-muted-foreground">
                          {String(o.occasion_day).padStart(2, "0")}/{String(o.occasion_month).padStart(2, "0")}
                        </span>
                      </div>
                    ))
                  )}
                </AccordionContent>
              </AccordionItem>

              <AccordionItem value="comms">
                <AccordionTrigger>
                  {isAr
                    ? `سجلّ الرسائل (${data.communications.length})`
                    : `Messages log (${data.communications.length})`}
                </AccordionTrigger>
                <AccordionContent className="space-y-2">
                  {data.communications.length === 0 ? (
                    <EmptyHint text={isAr ? "لا رسائل بعد." : "No messages yet."} />
                  ) : (
                    data.communications.map((c: any) => (
                      <div key={c.id} className="rounded border border-border p-3 text-xs">
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-2">
                            {c.channel === "whatsapp" ? (
                              <MessageCircle className="h-3 w-3 text-green-600" />
                            ) : (
                              <Bell className="h-3 w-3 text-blue-600" />
                            )}
                            <span className="font-medium">{c.purpose}</span>
                          </div>
                          <Badge
                            variant={
                              c.status === "sent"
                                ? "default"
                                : c.status === "failed"
                                  ? "destructive"
                                  : "secondary"
                            }
                            className="text-[10px]"
                          >
                            {c.status}
                          </Badge>
                        </div>
                        {c.subject && <div className="mt-1 font-medium">{c.subject}</div>}
                        <div className="mt-1 line-clamp-2 text-muted-foreground">{c.body}</div>
                        <div className="mt-1 text-[10px] text-muted-foreground">
                          {new Date(c.created_at).toLocaleString()}
                        </div>
                        {c.error && <div className="mt-1 text-[10px] text-destructive">{c.error}</div>}
                      </div>
                    ))
                  )}
                </AccordionContent>
              </AccordionItem>
            </Accordion>
          </div>
        )}
      </SheetContent>
    </Sheet>
  );
}

function SummaryRow({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex items-center justify-between gap-2">
      <span className="text-muted-foreground">{label}</span>
      <span className="font-medium">{value}</span>
    </div>
  );
}

function Stat({ label, value }: { label: string; value: string }) {
  return (
    <div>
      <div className="text-xs text-muted-foreground">{label}</div>
      <div className="mt-0.5 font-semibold">{value}</div>
    </div>
  );
}

function EmptyHint({ text }: { text: string }) {
  return <div className="rounded border border-dashed border-border p-4 text-center text-xs text-muted-foreground">{text}</div>;
}

// ─────────────────────────────────────────────────────────────────────────────
// Message Composer
// ─────────────────────────────────────────────────────────────────────────────
function MessageComposer({
  userId,
  open,
  onClose,
  isAr,
}: {
  userId: string | null;
  open: boolean;
  onClose: () => void;
  isAr: boolean;
}) {
  const qc = useQueryClient();
  const [chWhats, setChWhats] = React.useState(true);
  const [chInbox, setChInbox] = React.useState(true);
  const [purpose, setPurpose] = React.useState("custom");
  const [whatsBody, setWhatsBody] = React.useState("");
  const [titleAr, setTitleAr] = React.useState("");
  const [titleEn, setTitleEn] = React.useState("");
  const [titleFr, setTitleFr] = React.useState("");
  const [bodyAr, setBodyAr] = React.useState("");
  const [bodyEn, setBodyEn] = React.useState("");
  const [bodyFr, setBodyFr] = React.useState("");

  // refs لإدراج placeholders في موضع المؤشّر
  const whatsRef = React.useRef<HTMLTextAreaElement>(null);
  const bodyArRef = React.useRef<HTMLTextAreaElement>(null);
  const bodyEnRef = React.useRef<HTMLTextAreaElement>(null);

  const reset = () => {
    setWhatsBody(""); setTitleAr(""); setTitleEn(""); setTitleFr("");
    setBodyAr(""); setBodyEn(""); setBodyFr(""); setPurpose("custom");
  };


  const sendMut = useMutation({
    mutationFn: () => {
      const channels: ("whatsapp" | "inbox")[] = [];
      if (chWhats) channels.push("whatsapp");
      if (chInbox) channels.push("inbox");
      return sendCustomerMessage({
        data: {
          userId: userId!,
          purpose: purpose as any,
          channels,
          whatsappBody: chWhats ? whatsBody : undefined,
          inbox: chInbox
            ? {
                title_ar: titleAr, title_en: titleEn, title_fr: titleFr,
                body_ar: bodyAr, body_en: bodyEn, body_fr: bodyFr,
              }
            : undefined,
        },
      });
    },
    onSuccess: (res) => {
      const failed = res.results.filter((r) => !r.ok);
      if (failed.length === 0) {
        toast.success(isAr ? "أُرسلت بنجاح" : "Sent successfully");
      } else {
        toast.warning(isAr ? `أُرسلت جزئيّاً (${failed.length} فشل)` : `Partial: ${failed.length} failed`);
      }
      qc.invalidateQueries({ queryKey: ["crm-360", userId] });
      reset();
      onClose();
    },
    onError: (e: any) => toast.error(e?.message ?? "Error"),
  });

  return (
    <Dialog open={open} onOpenChange={(o) => !o && (reset(), onClose())}>
      <DialogContent className="max-h-[90vh] max-w-2xl overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{isAr ? "إرسال رسالة للعميل" : "Send message"}</DialogTitle>
        </DialogHeader>

        <div className="space-y-4">
          {/* Purpose */}
          <div>
            <Label>{isAr ? "السبب" : "Purpose"}</Label>
            <Select value={purpose} onValueChange={setPurpose}>
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="custom">{isAr ? "مخصّص" : "Custom"}</SelectItem>
                <SelectItem value="welcome">{isAr ? "ترحيب" : "Welcome"}</SelectItem>
                <SelectItem value="thank_you">{isAr ? "شكر" : "Thank you"}</SelectItem>
                <SelectItem value="campaign">{isAr ? "حملة" : "Campaign"}</SelectItem>
                <SelectItem value="comeback">{isAr ? "حافز عودة" : "Comeback"}</SelectItem>
                <SelectItem value="occasion_reminder">{isAr ? "تذكير مناسبة" : "Occasion"}</SelectItem>
                <SelectItem value="segment_reward">{isAr ? "مكافأة شريحة" : "Reward"}</SelectItem>
              </SelectContent>
            </Select>
          </div>

          {/* Channels */}
          <div className="space-y-2 rounded border border-border p-3">
            <Label className="text-sm">{isAr ? "القنوات" : "Channels"}</Label>
            <label className="flex items-center gap-2 text-sm">
              <Checkbox checked={chWhats} onCheckedChange={(v) => setChWhats(!!v)} />
              <MessageCircle className="h-4 w-4 text-green-600" /> {isAr ? "واتساب" : "WhatsApp"}
            </label>
            <label className="flex items-center gap-2 text-sm">
              <Checkbox checked={chInbox} onCheckedChange={(v) => setChInbox(!!v)} />
              <Bell className="h-4 w-4 text-blue-600" /> {isAr ? "إشعار داخل التطبيق" : "In-app inbox"}
            </label>
          </div>

          {/* WhatsApp body */}
          {chWhats && (
            <div className="space-y-2">
              <Label>{isAr ? "نصّ الواتساب (بلغة العميل)" : "WhatsApp body (customer's language)"}</Label>
              <PlaceholderChips inputRef={whatsRef} onChange={setWhatsBody} />
              <Textarea
                ref={whatsRef}
                rows={4}
                value={whatsBody}
                onChange={(e) => setWhatsBody(e.target.value)}
                placeholder={isAr ? "مرحباً {{first_name}}…" : "Hello {{first_name}}…"}
              />
            </div>
          )}

          {/* Inbox bilingual (ar/en) — الفرنسيّة معطَّلة */}
          {chInbox && (
            <Tabs defaultValue="ar">
              <TabsList className="grid grid-cols-2">
                <TabsTrigger value="ar">العربيّة</TabsTrigger>
                <TabsTrigger value="en">English</TabsTrigger>
              </TabsList>
              <TabsContent value="ar" className="space-y-2">
                <Input value={titleAr} onChange={(e) => setTitleAr(e.target.value)} placeholder="عنوان" dir="rtl" />
                <PlaceholderChips inputRef={bodyArRef} onChange={setBodyAr} />
                <Textarea ref={bodyArRef} rows={3} value={bodyAr} onChange={(e) => setBodyAr(e.target.value)} placeholder="المحتوى" dir="rtl" />
              </TabsContent>
              <TabsContent value="en" className="space-y-2">
                <Input value={titleEn} onChange={(e) => setTitleEn(e.target.value)} placeholder="Title" />
                <PlaceholderChips inputRef={bodyEnRef} onChange={setBodyEn} />
                <Textarea ref={bodyEnRef} rows={3} value={bodyEn} onChange={(e) => setBodyEn(e.target.value)} placeholder="Body" />
              </TabsContent>
            </Tabs>
          )}

        </div>

        <DialogFooter className="gap-2">
          <Button variant="outline" onClick={() => (reset(), onClose())}>
            {isAr ? "إلغاء" : "Cancel"}
          </Button>
          <Button
            onClick={() => sendMut.mutate()}
            disabled={
              sendMut.isPending ||
              (!chWhats && !chInbox) ||
              (chWhats && !whatsBody.trim()) ||
              (chInbox && !bodyEn.trim() && !bodyAr.trim())
            }
          >
            {sendMut.isPending ? <Loader2 className="me-2 h-4 w-4 animate-spin" /> : <Send className="me-2 h-4 w-4" />}
            {isAr ? "إرسال" : "Send"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Live Visitors strip — أعلى لوحة CRM
// ─────────────────────────────────────────────────────────────────────────────
function LiveVisitorsStrip({ isAr }: { isAr: boolean }) {
  const [showList, setShowList] = React.useState(false);

  const statsQ = useQuery({
    queryKey: ["live-visitors-stats"],
    queryFn: () => getLiveVisitorsStats({ data: {} }),
    refetchInterval: 60_000,
  });

  const listQ = useQuery({
    queryKey: ["live-visitors-list"],
    queryFn: () => getLiveVisitorsList({ data: {} }),
    enabled: showList,
    refetchInterval: showList ? 60_000 : false,
  });

  const now = statsQ.data?.now ?? 0;
  const today = statsQ.data?.today ?? 0;

  return (
    <>
      <div className="mb-4 flex flex-wrap items-center gap-3 rounded-none border border-border bg-card p-3">
        <button
          onClick={() => setShowList(true)}
          className="flex items-center gap-2 rounded-none px-2 py-1 text-sm transition-colors hover:bg-muted"
        >
          <span className="relative flex h-2.5 w-2.5">
            <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-green-500 opacity-60" />
            <span className="relative inline-flex h-2.5 w-2.5 rounded-full bg-green-600" />
          </span>
          <Radio className="h-3.5 w-3.5 text-muted-foreground" />
          <span className="font-medium">{now}</span>
          <span className="text-muted-foreground">{isAr ? "زائر الآن" : "now"}</span>
        </button>
        <div className="text-sm text-muted-foreground">·</div>
        <div className="text-sm">
          <Eye className="me-1 inline h-3.5 w-3.5 text-muted-foreground" />
          <span className="font-medium">{today}</span>{" "}
          <span className="text-muted-foreground">{isAr ? "اليوم" : "today"}</span>
        </div>
      </div>

      <Sheet open={showList} onOpenChange={setShowList}>
        <SheetContent className="w-full overflow-y-auto sm:max-w-md">
          <SheetHeader>
            <SheetTitle>{isAr ? "الزوّار الآن" : "Live visitors"}</SheetTitle>
          </SheetHeader>
          <div className="mt-4 space-y-2">
            {listQ.isLoading ? (
              <div className="flex justify-center py-10">
                <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
              </div>
            ) : (listQ.data ?? []).length === 0 ? (
              <EmptyHint text={isAr ? "لا أحد متّصل الآن." : "Nobody online right now."} />
            ) : (
              (listQ.data ?? []).map((s: any) => (
                <VisitorCard key={s.session_id} s={s} isAr={isAr} />
              ))
            )}
          </div>
        </SheetContent>
      </Sheet>
    </>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// VisitorCard — بطاقة زائر مفصّلة + إرسال همسة فوريّة
// ─────────────────────────────────────────────────────────────────────────────
function fmtDuration(sec: number): string {
  if (sec < 60) return `${sec}s`;
  const m = Math.floor(sec / 60);
  if (m < 60) return `${m}m`;
  const h = Math.floor(m / 60);
  return `${h}h ${m % 60}m`;
}

function shortenReferrer(r?: string | null): string | null {
  if (!r) return null;
  try {
    const u = new URL(r);
    return u.hostname.replace(/^www\./, "");
  } catch {
    return r.slice(0, 30);
  }
}

const WHISPER_TEMPLATES = (locale: "ar" | "en", name?: string | null) => {
  const who = name?.split(" ")[0] ?? (locale === "ar" ? "هلا" : "Hi");
  return locale === "ar"
    ? [
        { label: "👋 ترحيب", body: `${who}! أهلاً بك في ليكمو 🌹 أنا هنا إن احتجت أيّ مساعدة في اختيار العطر المناسب.` },
        { label: "🛒 ساعدني", body: `لاحظت أنّك تتصفّح المجموعة الآن. هل أساعدك في اختيار عطر مميّز؟ كلّ عطر له شخصيّته وأنا أعرفها جيّداً ✨` },
        { label: "🎁 هديّة؟", body: `هل تبحث عن هديّة؟ يسعدني اقتراح عطر يناسب الشخصيّة والمناسبة 🎁` },
      ]
    : [
        { label: "👋 Welcome", body: `Hi ${who}! Welcome to Lecmo 🌹 I'm here if you need help choosing the right fragrance.` },
        { label: "🛒 Help", body: `I noticed you're browsing our collection. Would you like help finding the perfect scent? ✨` },
        { label: "🎁 Gifting?", body: `Looking for a gift? I'd love to suggest a fragrance that matches the person and occasion 🎁` },
      ];
};

function VisitorCard({ s, isAr }: { s: any; isAr: boolean }) {
  const sendWhisper = useServerFn(sendVisitorWhisper);
  const [open, setOpen] = React.useState(false);
  const [body, setBody] = React.useState("");
  const [sending, setSending] = React.useState(false);
  const whisperRef = React.useRef<HTMLTextAreaElement>(null);


  const ref = shortenReferrer(s.referrer);
  const templates = WHISPER_TEMPLATES(isAr ? "ar" : "en", s.display_name);

  async function handleSend(text?: string) {
    const finalText = (text ?? body).trim();
    if (!finalText) return;
    setSending(true);
    try {
      await sendWhisper({
        data: {
          sessionId: s.session_id,
          userId: s.user_id ?? null,
          body: finalText,
          locale: isAr ? "ar" : "en",
        },
      });
      toast.success(isAr ? "أُرسلت الهمسة ✨" : "Whisper sent ✨");
      setBody("");
      setOpen(false);
    } catch (e: any) {
      toast.error(e?.message ?? "Failed");
    } finally {
      setSending(false);
    }
  }

  return (
    <div className="rounded border border-border p-3 text-xs">
      <div className="flex items-center justify-between gap-2">
        <span className="font-medium text-sm">
          {s.display_name ?? (isAr ? "زائر" : "Visitor")}
          {!s.user_id && (
            <span className="ms-1.5 text-[10px] text-muted-foreground">
              ({isAr ? "مجهول" : "guest"})
            </span>
          )}
        </span>
        <Badge variant="outline" className="text-[10px]">{s.market ?? "—"}</Badge>
      </div>

      <div className="mt-2 grid grid-cols-2 gap-1.5 text-[11px] text-muted-foreground">
        <div>📍 {s.current_path ?? "/"}</div>
        <div>📱 {s.device ?? "—"} · {s.country ?? "—"}</div>
        <div>⏱️ {fmtDuration(s.duration_sec ?? 0)}</div>
        <div>📄 {s.page_count ?? 0} {isAr ? "صفحة" : "pages"}</div>
        {s.cart_count > 0 && (
          <div className="text-orange-600">🛒 {s.cart_count} {isAr ? "في السلّة" : "in cart"}</div>
        )}
        {s.wishlist_count > 0 && (
          <div className="text-pink-600">❤️ {s.wishlist_count} {isAr ? "أمنية" : "wished"}</div>
        )}
        {ref && <div className="col-span-2">🔗 {isAr ? "من" : "via"} {ref}</div>}
        {s.last_product_title && (
          <div className="col-span-2 truncate">
            👀 {isAr ? "آخر منتج: " : "Last: "}{s.last_product_title}
          </div>
        )}
      </div>

      <div className="mt-2 text-[10px] text-muted-foreground">
        {isAr ? "آخر نشاط: " : "Last seen: "}
        {new Date(s.last_seen_at).toLocaleTimeString()}
      </div>

      {/* Whisper section */}
      {!open ? (
        <Button
          size="sm"
          variant="outline"
          className="mt-2 h-7 w-full text-[11px]"
          onClick={() => setOpen(true)}
        >
          <Sparkles className="me-1.5 h-3 w-3" />
          {isAr ? "همسة فوريّة عبر الوكيل" : "Send live whisper"}
        </Button>
      ) : (
        <div className="mt-2 space-y-2 rounded border border-primary/20 bg-primary/5 p-2">
          <div className="flex flex-wrap gap-1">
            {templates.map((tpl) => (
              <button
                key={tpl.label}
                type="button"
                onClick={() => handleSend(tpl.body)}
                disabled={sending}
                className="rounded border border-border bg-background px-1.5 py-0.5 text-[10px] hover:bg-muted disabled:opacity-50"
              >
                {tpl.label}
              </button>
            ))}
          </div>
          {/* أدرج placeholder — أساسيّات وروابط فقط (لا حاجة لتفاصيل الطلب في همسة فوريّة) */}
          <PlaceholderChips inputRef={whisperRef} onChange={setBody} groups={["basics", "links"]} />
          <Textarea
            ref={whisperRef}
            value={body}
            onChange={(e) => setBody(e.target.value)}
            placeholder={isAr ? "اكتب رسالة مخصّصة…" : "Write a custom message…"}
            rows={2}
            className="text-[11px]"
            maxLength={500}
          />

          <div className="flex items-center justify-end gap-1.5">
            <Button size="sm" variant="ghost" className="h-6 text-[10px]" onClick={() => setOpen(false)}>
              {isAr ? "إلغاء" : "Cancel"}
            </Button>
            <Button
              size="sm"
              className="h-6 text-[10px]"
              onClick={() => handleSend()}
              disabled={sending || !body.trim()}
            >
              {sending ? (
                <Loader2 className="h-3 w-3 animate-spin" />
              ) : (
                <>
                  <Send className="me-1 h-3 w-3" />
                  {isAr ? "إرسال" : "Send"}
                </>
              )}
            </Button>
          </div>
          <p className="text-[10px] text-muted-foreground">
            {isAr
              ? "ستظهر فوراً في bubble الوكيل عند الزائر."
              : "Appears instantly in the visitor's agent bubble."}
          </p>
        </div>
      )}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Activity Timeline — تبويب «النشاط» في ملفّ العميل
// ─────────────────────────────────────────────────────────────────────────────
const EVENT_META: Record<string, { icon: any; cls: string; ar: string; en: string }> = {
  page_view:       { icon: Eye,         cls: "text-muted-foreground", ar: "تصفّح",        en: "Viewed" },
  add_to_cart:     { icon: ShoppingBag, cls: "text-orange-600",       ar: "أضاف للسلّة",  en: "Added to cart" },
  wishlist_add:    { icon: Heart,       cls: "text-pink-600",         ar: "أضاف لأمنياته",en: "Wishlisted" },
  wishlist_remove: { icon: Heart,       cls: "text-muted-foreground", ar: "أزال من أمنياته", en: "Removed wish" },
  login:           { icon: LogIn,       cls: "text-blue-600",         ar: "سجّل الدخول",  en: "Signed in" },
  signup:          { icon: User,        cls: "text-blue-600",         ar: "أنشأ حساباً",  en: "Signed up" },
  begin_checkout:  { icon: CreditCard,  cls: "text-green-600",        ar: "بدأ الدفع",    en: "Begin checkout" },
  agent_open:      { icon: MessageCircle, cls: "text-purple-600",     ar: "فتح الوكيل",   en: "Opened agent" },
};

function ActivityTimelineTab({ userId, isAr }: { userId: string; isAr: boolean }) {
  const q = useQuery({
    queryKey: ["customer-timeline", userId],
    queryFn: () => getCustomerTimeline({ data: { userId, limit: 100 } }),
    enabled: !!userId,
  });

  if (q.isLoading) {
    return <div className="flex justify-center py-6"><Loader2 className="h-4 w-4 animate-spin text-muted-foreground" /></div>;
  }
  const events = q.data ?? [];
  if (events.length === 0) {
    return <EmptyHint text={isAr ? "لا نشاط بعد." : "No activity yet."} />;
  }

  return (
    <div className="space-y-1.5">
      {events.map((e: any) => {
        const meta = EVENT_META[e.event_type] ?? { icon: Activity, cls: "text-muted-foreground", ar: e.event_type, en: e.event_type };
        const Icon = meta.icon;
        const label = isAr ? meta.ar : meta.en;
        return (
          <div key={e.id} className="flex items-start gap-2 rounded border border-border/50 p-2 text-xs">
            <Icon className={`mt-0.5 h-3.5 w-3.5 shrink-0 ${meta.cls}`} />
            <div className="min-w-0 flex-1">
              <div className="flex items-center gap-1.5">
                <span className="font-medium">{label}</span>
                {e.product_title && <span className="text-muted-foreground truncate">· {e.product_title}</span>}
              </div>
              <div className="mt-0.5 flex flex-wrap gap-x-2 text-[10px] text-muted-foreground">
                {e.path && <span className="truncate">{e.path}</span>}
                <span>· {new Date(e.created_at).toLocaleString()}</span>
                {e.market && <span>· {e.market}</span>}
              </div>
            </div>
          </div>
        );
      })}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Wishlist tab
// ─────────────────────────────────────────────────────────────────────────────
function WishlistTab({ userId, isAr }: { userId: string; isAr: boolean }) {
  const q = useQuery({
    queryKey: ["customer-wishlist", userId],
    queryFn: () => getCustomerWishlist({ data: { userId } }),
    enabled: !!userId,
  });

  if (q.isLoading) {
    return <div className="flex justify-center py-6"><Loader2 className="h-4 w-4 animate-spin text-muted-foreground" /></div>;
  }
  const items = q.data ?? [];
  if (items.length === 0) {
    return <EmptyHint text={isAr ? "لا منتجات محفوظة." : "No saved products."} />;
  }

  return (
    <div className="space-y-2">
      {items.map((it: any) => (
        <div key={it.product_handle} className="flex items-center gap-3 rounded border border-border p-2 text-xs">
          {it.product_image && (
            <img src={it.product_image} alt="" className="h-12 w-12 rounded object-cover" />
          )}
          <div className="min-w-0 flex-1">
            <div className="truncate font-medium">{it.product_title ?? it.product_handle}</div>
            <div className="mt-0.5 text-[10px] text-muted-foreground">
              {it.product_price && `${it.product_price} ${it.product_currency ?? ""}`}
              {it.created_at && ` · ${new Date(it.created_at).toLocaleDateString()}`}
            </div>
          </div>
        </div>
      ))}
    </div>
  );
}
